import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import { toast } from 'react-toastify';
import GooglePlacesAutocomplete, { geocodeByPlaceId } from 'react-google-places-autocomplete';
import EntitySearchPicker, { getEntityDisplayName, getEntityId } from '../components/EntitySearchPicker';
import { loadDropdownData, addAddressToEntity, loadEntityAddressItems, getAddressesByEntityId, getEntityById, createEntityAddressItem, loadEntityModules } from '../customer/onboardingApi';
import ItemsStep from '../customer/steps/ItemsStep';
import StepBar from '../components/StepBar';

// ── Steps ─────────────────────────────────────────────────────
const STEPS = [
    { key: 'search', label: 'Find Entity' },
    { key: 'select-address', label: 'Address' },
    { key: 'items', label: 'Items' },
    { key: 'review', label: 'Review' },
    { key: 'done', label: 'Done' },
];

// ── Attribute builder (mirrors onboardingApi helper) ──────────
function buildItemAttributeValues(item) {
    const a = {};
    if (item.length) a['31'] = item.length;
    if (item.width) a['32'] = item.width;
    if (item.depth) a['33'] = item.depth;
    if (item.shape) a['34'] = item.shape;
    if (item.max_fill_level) a['45'] = item.max_fill_level;
    if (item.capacity) a['3'] = item.capacity;
    if (item.net_capacity) a['39'] = item.net_capacity;
    if (item.latitude) a['46'] = item.latitude;
    if (item.longitude) a['47'] = item.longitude;
    return a;
}

// ── Blank item ────────────────────────────────────────────────
function blankItem(activeStatusId) {
    return {
        id: crypto.randomUUID(),
        name: '',
        map_identifier: '',
        item_id: 0,
        address_index: 0,
        icon_id: 0,
        status_type_id: activeStatusId,
        is_default: false,
        length: '',
        width: '',
        depth: '',
        shape: '',
        max_fill_level: '',
        capacity: '',
        net_capacity: '',
        latitude: '',
        longitude: '',
        provider_ids: [],
    };
}

/**
 * Get the ID of the "Active" status from dropdown data
 */
function getActiveStatusId(dropdowns) {
    if (!dropdowns || !dropdowns.statusTypes) return '';
    return (
        dropdowns.statusTypes.find(
            (s) => s?.StatusType?.status_name?.toLowerCase() === 'active',
        )?.status_type_id || dropdowns.statusTypes[0]?.status_type_id || ''
    );
}

// ── Extract addresses from entity response ────────────────────
function extractAddresses(entityData) {
    const included = entityData?.data?.included ?? entityData?.included;
    if (Array.isArray(included?.moduleLocations) && included.moduleLocations.length > 0) {
        return included.moduleLocations;
    }

    const attrs = entityData?.data?.attributes || entityData?.attributes || entityData;
    // Try moduleLocations (standard Rivio entity shape)
    if (Array.isArray(attrs?.moduleLocations) && attrs.moduleLocations.length > 0) {
        return attrs.moduleLocations;
    }
    // Try addresses array
    if (Array.isArray(attrs?.addresses) && attrs.addresses.length > 0) {
        return attrs.addresses;
    }
    return [];
}

function toAddressFormData(loc) {
    const addr = loc?.Address || loc;
    return {
        id: String(addr?.address_id ?? loc?.address_id ?? crypto.randomUUID()),
        address_type_id: addr?.address_type_id ?? 0,
        name: addr?.name ?? loc?.name ?? '',
        street_address1: addr?.street_address1 ?? '',
        street_address2: addr?.street_address2 ?? '',
        city: addr?.city ?? '',
        state: addr?.state ?? '',
        postal_code: addr?.postal_code ?? '',
        country: addr?.country ?? 'US',
        latitude: addr?.latitude ?? undefined,
        longitude: addr?.longitude ?? undefined,
        status_type_id: addr?.status_type_id ?? 1,
    };
}

function getAddressLine(loc) {
    const addr = loc?.Address || loc;
    const parts = [addr?.street_address1, addr?.city, addr?.state].filter(Boolean);
    return parts.join(', ') || 'Unknown address';
}

function getAddressLabel(loc) {
    const addr = loc?.Address || loc;
    return addr?.name || loc?.name || 'Address';
}

function getAddressId(loc) {
    return loc?.Address?.address_id ?? loc?.address_id ?? 0;
}

function extractAddressesFromEntityAddressItems(rows) {
    const byAddressId = new Map();

    rows.forEach((row) => {
        const address = row?.Address ?? row?.attributes?.Address;
        const addressId = address?.address_id ?? row?.address_id ?? row?.attributes?.address_id;
        if (!addressId || byAddressId.has(addressId)) return;
        byAddressId.set(addressId, {
            ...address,
            address_id: addressId,
            name: address?.name ?? row?.address_name ?? row?.name ?? 'Address',
        });
    });

    return Array.from(byAddressId.values());
}

export default function AddItemsWizard({ onComplete, onCancel, initialEntity = null } = {}) {
    const [step, setStep] = useState('search');
    // A device claim passes its selected customer so it need not be found again.
    const [selectedEntity, setSelectedEntity] = useState(initialEntity);
    const [entityAddresses, setEntityAddresses] = useState([]);
    const [loadingAddresses, setLoadingAddresses] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [dropdowns, setDropdowns] = useState(null);
    const [items, setItems] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [createdItems, setCreatedItems] = useState([]);

    // Load dropdowns on mount
    useEffect(() => {
        loadDropdownData().then(d => {
            setDropdowns({...d, entityStatusOptions: d.statusTypes});
            const activeId = getActiveStatusId(d);
            setItems([blankItem(activeId)]);
        }).catch(() => toast.error('Failed to load form options'));
    }, []);

    // Load entity addresses when entity is selected
    const loadEntityAddresses = async (entity) => {
        setLoadingAddresses(true);
        try {
            const entityId = getEntityId(entity);

            // Layer 1: try entity-address-items (entities that already have items)
            const itemsRes = await loadEntityModules(entityId, {
                page_size: 200,
                fetchAll: true,
            });
            const itemRows = Array.isArray(itemsRes?.data)
                ? itemsRes.data
                : Array.isArray(itemsRes?.data?.data)
                    ? itemsRes.data.data
                    : [];

            let locs = extractAddressesFromEntityAddressItems(itemRows);

            // Layer 2: GET /entities/{id} → data.included.moduleLocations
            if (locs.length === 0) {
                const data = await getEntityById(entityId);
                locs = extractAddresses(data);
            }

            // Layer 3: GET /addresses?entity_id=X direct lookup
            if (locs.length === 0) {
                const directAddresses = await getAddressesByEntityId(entityId);
                if (directAddresses.length > 0) {
                    locs = directAddresses;
                }
            }

            setEntityAddresses(locs);
            if (locs.length === 0) {
                toast('No addresses found for this entity. Add an address first.', { icon: '⚠️' });
            }
        } catch (err) {
            console.error('[AddItems] loadEntityAddresses error:', err);
            toast.error('Failed to load entity addresses');
            setEntityAddresses([]);
        } finally {
            setLoadingAddresses(false);
        }
    };

    const handleEntitySelect = (entity) => {
        setSelectedEntity(entity);
        setSelectedLocation(null);
        setEntityAddresses([]);
    };

    const handleProceedToSelectAddress = () => {
        if (!selectedEntity) return;
        setStep('select-address');
        loadEntityAddresses(selectedEntity);
    };

    const handleSubmit = async () => {
        if (!selectedEntity || !selectedLocation || items.length === 0) return;
        setSubmitting(true);
        const entityId = getEntityId(selectedEntity);
        const addressId = getAddressId(selectedLocation);
        const activeId = dropdowns ? getActiveStatusId(dropdowns) : 1;
        const results = [];
        try {
            for (const item of items) {
                if (!item.name.trim() || !item.item_id) continue;
                const payload = {
                    name: item.name,
                    entity_id: entityId,
                    address_id: addressId,
                    item_id: item.item_id,
                    status_type_id: item.status_type_id || activeId,
                    map_identifier: item.map_identifier || undefined,
                    provider_ids: item.provider_ids || [],
                    attributes: item?.attributes ?? buildItemAttributeValues(item?.attributes),
                };
                const res = await createEntityAddressItem(payload);

                results.push(res);
            }
            setCreatedItems(results);
            setStep('done');
            toast.success(`${results.length} item(s) added successfully!`);
            if (onComplete) {
                try {
                    onComplete({ entityId, addressId, results });
                } catch (callbackError) {
                    console.error('Assets saved; installation return failed', callbackError);
                    toast.error('Assets saved, but returning to installation failed. Do not create them again.');
                }
            }
        } catch (err) {
            toast.error(err.message || 'Failed to create items');
        } finally {
            setSubmitting(false);
        }
    };

    const validItems = items.filter(i => i.name.trim() && i.item_id > 0);
    const addressFormData = selectedLocation
        ? [toAddressFormData(selectedLocation)]
        : [];

    return (
        <MDBox sx={{ mx: 'auto' }}>
            {step !== 'done' && <StepBar current={step} STEPS={STEPS} />}

            {/* ── Step 1: Search entity ── */}
            {step === 'search' && (
                <MDBox sx={{ backgroundColor: 'rgb(38, 40, 51)', border: '1px solid gray', borderRadius: '12px', p: 3 }}>
                    <MDTypography variant="h6" color="white" fontWeight="bold">Find the Entity</MDTypography>
                    <MDTypography variant="button" color="white" opacity={0.6} sx={{ display: 'block', mb: 3 }}>
                        Search for the customer or entity you want to add an address to.
                    </MDTypography>

                    <EntitySearchPicker
                        selectedEntity={selectedEntity}
                        onSelect={setSelectedEntity}
                    />

                    <MDBox mt={4} display="flex" justifyContent="flex-end">
                        <MDButton
                            disabled={!selectedEntity}
                            onClick={handleProceedToSelectAddress}
                            variant="gradient"
                            color="info"
                            sx={{ gap: 1 }}
                        >
                            Continue <i className="fa fa-chevron-right" />
                        </MDButton>
                    </MDBox>
                </MDBox>
            )}

            {/* ── Step 2: Select address ── */}
            {step === 'select-address' && selectedEntity && (
                <MDBox sx={{ backgroundColor: 'rgb(38, 40, 51)', border: '1px solid gray', borderRadius: '12px', p: 3 }}>

                    <MDTypography
                        variant="h6"
                        color="white"
                        fontWeight="bold"
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                        <i className="fa fa-map-marker-alt" style={{ color: '#FACC15' }} />
                        Select Address
                    </MDTypography>

                    <MDTypography variant="button" color="white" opacity={0.6} sx={{ display: 'block', mb: 3 }}>
                        Choose which address the items will be added to for{' '}
                        <span style={{ color: 'white', fontWeight: 500 }}>
                            {getEntityDisplayName(selectedEntity)}
                        </span>
                    </MDTypography>

                    {loadingAddresses ? (
                        <MDBox display="flex" alignItems="center" justifyContent="center" py={4} gap={1}>
                            <i className="fa fa-spinner fa-spin" style={{ color: '#9CA3AF' }} />
                            <MDTypography variant="button" color="white" opacity={0.6}>
                                Loading addresses…
                            </MDTypography>
                        </MDBox>
                    ) : entityAddresses.length === 0 ? (
                        <MDBox textAlign="center" py={4}>
                            <i className="fa fa-map-marker-alt" style={{ color: '#4B5563', fontSize: '2rem', marginBottom: '1rem' }} />
                            <MDTypography variant="button" color="white" opacity={0.6} mb={2} display="block">
                                No addresses found for this entity.
                            </MDTypography>
                            <MDButton
                                component={Link}
                                to="/onboarding/add-address"
                                variant="gradient"
                                color="info"
                            >
                                Add an Address First
                            </MDButton>
                        </MDBox>
                    ) : (
                        <MDBox>
                            {entityAddresses.map((loc, i) => {
                                const addrId = getAddressId(loc);
                                const isSelected =
                                    selectedLocation &&
                                    getAddressId(selectedLocation) === addrId;

                                return (
                                    <MDBox
                                        key={addrId || i}
                                        onClick={() => setSelectedLocation(loc)}
                                        sx={{
                                            p: 2,
                                            mb: 1,
                                            borderRadius: '8px',
                                            border: isSelected ? '1px solid #49a3f1' : '1px solid gray',
                                            backgroundColor: 'transparent',
                                            cursor: 'pointer',
                                            transition: '0.2s',
                                            '&:hover': {
                                                borderColor: '#4ADE80',
                                                backgroundColor: '#49a3f1',
                                            },
                                        }}
                                    >
                                        <MDBox display="flex" alignItems="center" gap={2}>
                                            <i
                                                className={`fa ${isSelected ? 'fa-dot-circle' : 'fa-circle'}`}
                                                style={{ color: isSelected ? '#49a3f1' : 'gray' }}
                                            />
                                            <MDBox>
                                                <MDTypography variant="button" fontWeight="bold" color="white" display="block">
                                                    {getAddressLabel(loc)}
                                                </MDTypography>
                                                <MDTypography variant="caption" color="white" opacity={0.6}>
                                                    {getAddressLine(loc)}
                                                </MDTypography>
                                            </MDBox>
                                        </MDBox>
                                    </MDBox>
                                );
                            })}
                        </MDBox>
                    )}

                    <MDBox mt={4} display="flex" justifyContent="space-between">
                        <MDButton
                            onClick={() => setStep('search')}
                            variant="outlined"
                            color="secondary"
                            sx={{ gap: 1 }}
                        >
                            <i className="fa fa-chevron-left" /> Back
                        </MDButton>

                        {onCancel && <MDButton variant="outlined" color="secondary"
                            disabled={submitting} onClick={onCancel}>
                            Back to device installation
                        </MDButton>}

                        <MDButton
                            disabled={!selectedLocation}
                            onClick={() => setStep('items')}
                            variant="gradient"
                            color="info"
                            sx={{ gap: 1 }}
                        >
                            Add Items <i className="fa fa-chevron-right" />
                        </MDButton>
                    </MDBox>
                </MDBox>
            )}

            {/* ── Step 3: Items ── */}
            {step === 'items' && dropdowns && selectedLocation && (
                <MDBox>

                    <MDBox
                        mb={3}
                        p={2}
                        sx={{ backgroundColor: 'rgb(38, 40, 51)', border: '1px solid gray', borderRadius: '8px' }}
                    >
                        <MDBox display="flex" alignItems="center" gap={2}>
                            <i className="fa fa-map-marker-alt" style={{ color: '#FACC15' }} />
                            <MDBox>
                                <MDTypography variant="button" fontWeight="bold" color="white" display="block">
                                    {getAddressLabel(selectedLocation)}
                                </MDTypography>
                                <MDTypography variant="caption" color="white" opacity={0.6}>
                                    {getAddressLine(selectedLocation)}
                                </MDTypography>
                            </MDBox>
                        </MDBox>
                    </MDBox>

                    <ItemsStep
                        items={items}
                        addresses={addressFormData}
                        setItems={setItems}
                        dropdowns={dropdowns}
                    />

                    <MDBox mt={4} display="flex" justifyContent="space-between">
                        <MDButton
                            onClick={() => setStep('select-address')}
                            variant="outlined"
                            color="secondary"
                            sx={{ gap: 1 }}
                        >
                            <i className="fa fa-chevron-left" /> Back
                        </MDButton>

                        {onCancel && <MDButton variant="outlined" color="secondary"
                            disabled={submitting} onClick={onCancel}>
                            Back to device installation
                        </MDButton>}

                        <MDButton
                            disabled={validItems.length === 0}
                            onClick={() => setStep('review')}
                            variant="gradient"
                            color="info"
                            sx={{ gap: 1 }}
                        >
                            Review ({validItems.length}) <i className="fa fa-chevron-right" />
                        </MDButton>
                    </MDBox>
                </MDBox>
            )}

            {/* ── Step 4: Review ── */}
            {step === 'review' && selectedEntity && selectedLocation && (
                <MDBox sx={{ backgroundColor: 'rgb(38, 40, 51)', border: '1px solid gray', borderRadius: '12px', p: 3 }}>

                    <MDTypography variant="h6" color="white" fontWeight="bold" mb={3}>
                        Review & Confirm
                    </MDTypography>

                    <MDBox mb={3} p={2} sx={{ backgroundColor: 'rgb(38, 40, 51)', border: '1px solid gray', borderRadius: '8px' }}>
                        <MDTypography variant="caption" color="white" fontWeight="bold" textTransform="uppercase" sx={{ opacity: 0.5 }}>
                            Entity
                        </MDTypography>
                        <MDTypography variant="button" fontWeight="bold" color="white" display="block">
                            {getEntityDisplayName(selectedEntity)}
                        </MDTypography>
                    </MDBox>

                    <MDBox mb={3} p={2} sx={{ backgroundColor: 'rgb(38, 40, 51)', border: '1px solid gray', borderRadius: '8px' }}>
                        <MDTypography variant="caption" color="white" fontWeight="bold" textTransform="uppercase" sx={{ opacity: 0.5 }}>
                            Address
                        </MDTypography>
                        <MDTypography variant="button" fontWeight="bold" color="white" display="block">
                            {getAddressLabel(selectedLocation)}
                        </MDTypography>
                        <MDTypography variant="caption" color="white" opacity={0.6}>
                            {getAddressLine(selectedLocation)}
                        </MDTypography>
                    </MDBox>

                    <MDBox p={2} sx={{ backgroundColor: 'rgb(38, 40, 51)', border: '1px solid gray', borderRadius: '8px' }}>
                        <MDTypography variant="caption" color="white" fontWeight="bold" textTransform="uppercase" sx={{ opacity: 0.5, mb: 1 }}>
                            Items ({validItems.length})
                        </MDTypography>

                        {validItems.map(item => (
                            <MDBox key={item.id} display="flex" alignItems="center" gap={1} mb={1}>
                                <i className="fa fa-layer-group" style={{ color: '#4ADE80', fontSize: '0.75rem' }} />
                                <MDTypography variant="button" color="white">
                                    {item.name}
                                </MDTypography>
                                {item.capacity && (
                                    <MDTypography variant="caption" color="white" opacity={0.6}>
                                        · {item.capacity} gal
                                    </MDTypography>
                                )}
                            </MDBox>
                        ))}
                    </MDBox>

                    <MDBox mt={4} display="flex" justifyContent="space-between">
                        <MDButton
                            onClick={() => setStep('items')}
                            variant="outlined"
                            color="secondary"
                            sx={{ gap: 1 }}
                        >
                            <i className="fa fa-chevron-left" /> Edit Items
                        </MDButton>

                        {onCancel && <MDButton variant="outlined" color="secondary"
                            disabled={submitting} onClick={onCancel}>
                            Back to device installation
                        </MDButton>}

                        <MDButton
                            disabled={submitting}
                            onClick={handleSubmit}
                            variant="gradient"
                            color="success"
                            sx={{ gap: 1 }}
                        >
                            {submitting ? (
                                <><i className="fa fa-spinner fa-spin" /> Saving…</>
                            ) : (
                                <>Confirm & Save <i className="fa fa-chevron-right" /></>
                            )}
                        </MDButton>
                    </MDBox>
                </MDBox>
            )}

            {/* ── Step 5: Done ── */}
            {step === 'done' && selectedEntity && selectedLocation && (
                <MDBox sx={{ backgroundColor: 'rgb(38, 40, 51)', border: '1px solid #4ADE80', borderRadius: '12px', p: 4, textAlign: 'center' }}>

                    <i className="fa fa-check-circle" style={{ color: '#4ADE80', fontSize: '3rem', marginBottom: '1.5rem' }} />

                    <MDTypography variant="h5" color="white" fontWeight="bold" mb={1}>
                        Items Added!
                    </MDTypography>

                    <MDTypography variant="button" color="white" opacity={0.7} mb={3} display="block">
                        {createdItems.length} item(s) added to{' '}
                        <span style={{ fontWeight: 'bold', color: 'white' }}>
                            {getAddressLabel(selectedLocation)}
                        </span>{' '}
                        for{' '}
                        <span style={{ fontWeight: 'bold', color: 'white' }}>
                            {getEntityDisplayName(selectedEntity)}
                        </span>.
                    </MDTypography>

                    <MDBox display="flex" flexWrap="wrap" gap={2} justifyContent="center" mt={4}>
                        <MDButton component={Link} to="/onboarding/assign-device" variant="gradient" color="info" size="small">
                            Assign a Device
                        </MDButton>

                        {onCancel && <MDButton variant="outlined" color="secondary"
                            disabled={submitting} onClick={onCancel}>
                            Back to device installation
                        </MDButton>}

                        <MDButton
                            onClick={() => {
                                setStep('search');
                                setSelectedEntity(null);
                                setSelectedLocation(null);
                                setCreatedItems([]);
                            }}
                            variant="contained"
                            color="secondary"
                            size="small"
                        >
                            Add More Items
                        </MDButton>

                        <MDButton component={Link} to="/onboarding" variant="outlined" color="white" size="small">
                            Back to Onboarding
                        </MDButton>
                    </MDBox>
                </MDBox>
            )}
        </MDBox>
    );
}
